/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router";
import {
  ControlledInput,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useCommunities } from "../../store/useCommunities";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useCommunityWithdraws } from "../../store/useCommunityWithdraws";
import { CommunityWithdrawTable } from "./CommunityWithdrawTable";
import { CommunityDonationsTable } from "./CommunityDonationsTable";
import { useUsers } from "../../store/useUsers";
import { CommunityUsersTable } from "./CommunityUsersTable";
import { Switch } from "@material-tailwind/react";
import { useDropLocations } from "../../store/useDropLocations";
import { DropLocationInputItem } from "../../components/form/DynamicFormInput";

type Inputs = {
  name: string;
  description: string;
  website: string;
};

function CommunityDetail() {
  /** Params */
  const params = useParams();
  /** Hooks */
  const {
    getWithdraws,
    withdraws,
    getDonations,
    donations,
    hasNextDonations,
    clearDonations,
  } = useCommunityWithdraws();
  const { getCommunity, community } = useCommunities();
  const navigate = useNavigate();
  const { reset, control } = useForm<Inputs>();
  const { allCommunityUsers, getUsersByCommunity } = useUsers();
  const { getDropLocations } = useDropLocations();
  /** State */
  const [isFeatured, setIsFeatured] = useState(false);
  const [cities, setCities] = useState<DropLocationInputItem[]>([]);
  const [donationsPage, setDonationsPage] = useState<number>(1);

  /** Effects */
  useEffect(() => {
    getCommunity(params?.id as string);
    getWithdraws(params?.id as string, 1, 500);
    getUsersByCommunity(params?.id as string);
    getDropLocations();
    getDonations(params?.id as string, 1, 10);
    return () => {
      setCities([]);
      clearDonations();
    };
  }, []);

  useEffect(() => {
    if (community) {
      reset({
        name: community.name,
        description: community.description,
        website: community.website,
      });
      setCities(community?.cities?.map((ct) => ({ id: ct, value: ct })) ?? []);
      setIsFeatured(community?.isFeatured ?? false);
    }
  }, [community]);

  /** Callbacks */
  const goBack = () => navigate(-1);

  return (
    <div>
      <div className="flex flex-row items-center justify-between mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Community Details
        </h1>
      </div>
      {/* Top Stats */}
      <div className="flex flex-col sm:flex-row w-full p-2 sm:p-7 bg-white border border-gray-border drop-shadow-md sm:h-28 mb-1 sm:mb-10">
        <div className="flex flex-1 sm:flex-col justify-between items-center  border-b sm:border-b-0 sm:border-r border-gray-border h-full py-2 sm:py-0">
          <div className="flex items-center mb-1">
            <p className="font-satoshi text-2xl text-dark font-bold">
              €{Number(Number(community?.totalRevenue ?? 0) / 100)?.toFixed(2)}
            </p>
          </div>
          <p className="font-satoshi font-medium text-sm text-gray-text ml-2 sm:ml-0">
            Total Pay-out
          </p>
        </div>
        <div className="flex flex-1 sm:flex-col justify-between items-center  border-b sm:border-b-0 sm:border-r border-gray-border h-full py-2 sm:py-0">
          <div className="flex items-center mb-1">
            <p className="font-satoshi text-2xl text-dark font-bold">
              €{Number(Number(community?.balance ?? 0) / 100)?.toFixed(2)}
            </p>
          </div>
          <p className="font-satoshi font-medium text-sm text-gray-text ml-2 sm:ml-0">
            Current balance
          </p>
        </div>
        <div className="flex flex-1 sm:flex-col justify-between items-center  border-b sm:border-b-0 sm:border-r border-gray-border h-full py-2 sm:py-0">
          <div className="flex items-center mb-1">
            <p className="font-satoshi text-2xl text-dark font-bold">
              {community?.members}
            </p>
          </div>
          <p className="font-satoshi font-medium text-sm text-gray-text ml-2 sm:ml-0">
            Total members
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md mb-2 sm:mb-0">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Community Information
            </p>
          </div>
          <div className="w-full px-7 py-7">
            <ControlledInput
              control={control}
              label="Name"
              placeholder="Community name"
              name="name"
              disabled={true}
            />
            <ControlledInput
              control={control}
              label="Website"
              placeholder="Community website"
              name="website"
              disabled={true}
            />
            {/* Community logo - read only */}
            <div className="mb-4">
              <label className="block font-satoshi text-base font-medium text-dark mb-2">
                Community logo
              </label>
              {community?.image && (
                <img
                  src={community.image}
                  alt="Community logo"
                  className="w-32 h-32 object-cover border border-gray-border rounded"
                />
              )}
            </div>
            <div className="mb-4">
              <label className="block font-satoshi text-base font-medium text-dark mb-2">
                Is Featured
              </label>
              <Switch
                crossOrigin={"anonymous"}
                color="teal"
                ripple
                label="Is featured"
                checked={isFeatured}
                disabled={true}
              />
            </div>
            {/* Community featured background image - read only */}
            <div className="mb-4">
              <label className="block font-satoshi text-base font-medium text-dark mb-2">
                Featured background image
              </label>
              {community?.featuredImage && (
                <img
                  src={community.featuredImage}
                  alt="Featured background"
                  className="w-full max-w-md h-32 object-cover border border-gray-border rounded"
                />
              )}
            </div>
            <div className="mb-4">
              <label className="block font-satoshi text-base font-medium text-dark mb-2">
                Description
              </label>
              <div className="w-full p-3 border border-gray-border rounded bg-gray-50 min-h-[180px] font-satoshi text-base text-dark">
                {community?.description || "No description available"}
              </div>
            </div>
            <div className="mb-4">
              <label className="block font-satoshi text-base font-medium text-dark mb-2">
                Cities
              </label>
              <div className="flex flex-wrap gap-2">
                {cities.map((city, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 border border-gray-border rounded text-sm font-satoshi text-dark"
                  >
                    {city.value}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Back" />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12">
        <div className="flex w-full justify-between mb-3">
          <h2 className="font-satoshi text-xl font-bold text-dark">
            {community?.name}'s Withdraws
          </h2>
        </div>
        <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
          {!withdraws?.length ? (
            <p className="text-center font-satoshi text-base text-dark">
              There is no withdraw created yet.
            </p>
          ) : (
            <CommunityWithdrawTable communityId={params?.id as string} />
          )}
        </div>
      </div>

      <div className="mt-12">
        <div className="flex w-full justify-between mb-3">
          <h2 className="font-satoshi text-xl font-bold text-dark">
            Community members
          </h2>
        </div>
        <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
          {!allCommunityUsers?.length ? (
            <p className="text-center font-satoshi text-base text-dark">
              This community does not have any member yet.
            </p>
          ) : (
            <CommunityUsersTable />
          )}
        </div>
      </div>

      <div className="mt-12">
        <div className="flex w-full justify-between mb-3">
          <h2 className="font-satoshi text-xl font-bold text-dark">
            Community Donations
          </h2>
        </div>
        <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
          {!donations?.length ? (
            <p className="text-center font-satoshi text-base text-dark">
              No donations received yet.
            </p>
          ) : (
            <CommunityDonationsTable />
          )}
        </div>
        {hasNextDonations && (
          <div className="flex flex-row w-full items-center justify-end mt-3">
            <PrimaryButton
              onClick={() => {
                setDonationsPage(donationsPage + 1);
                getDonations(params?.id as string, donationsPage + 1, 10);
              }}
              label="Load more donations"
              className="h-10"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default withProtectedRoute(CommunityDetail);
